import {
  AccountEnvironment,
  ClientRepresentation,
  DeviceRepresentation,
  Group,
  LinkedAccountRepresentation,
  UserRepresentation,
  getApplications,
  getDevices,
  getGroups,
  getLinkedAccounts,
  getPersonalInfo,
  useEnvironment,
  usePromise,
} from "@keycloak/keycloak-account-ui";
import { label } from "@keycloak/keycloak-ui-shared";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import style from "./Hub.module.css";

/** Quick launch renders at most this many apps; the rest collapse into "+N more". */
const QUICK_LAUNCH_LIMIT = 8;

const HexMark = ({ size }: { size: number }) => (
  <svg
    className={style.heroMark}
    width={size}
    height={size}
    viewBox="0 0 200 200"
    role="img"
    aria-label="Atrial AI"
  >
    <path
      d="M100 15 L172 57 L172 143 L100 185 L28 143 L28 57 Z"
      fill="#041122"
      stroke="#F7F5FB"
      strokeWidth="6"
      strokeLinejoin="round"
    />
    <path
      d="M100 55 C 89 67, 78 90, 65 124 C 60 138, 64 152, 78 152 C 90 150, 94 138, 96 116 C 98 96, 99 76, 100 55 Z"
      fill="#0B00CF"
    />
    <path
      d="M100 55 C 111 67, 122 90, 135 124 C 140 138, 136 152, 122 152 C 110 150, 106 138, 104 116 C 102 96, 101 76, 100 55 Z"
      fill="#0B00CF"
    />
  </svg>
);

export const Hub = () => {
  const { t } = useTranslation();
  const context = useEnvironment<AccountEnvironment>();
  const { environment, keycloak } = context;
  const features = environment.features;

  const [user, setUser] = useState<UserRepresentation>();
  const [apps, setApps] = useState<ClientRepresentation[]>();
  const [devices, setDevices] = useState<DeviceRepresentation[]>();
  const [groups, setGroups] = useState<Group[]>();
  const [linked, setLinked] = useState<LinkedAccountRepresentation[]>();

  // Every fetch is independent and failure-tolerant: a tile with no data
  // renders its neutral state rather than blocking the page.
  usePromise(
    (signal) => getPersonalInfo({ signal, context }).catch(() => ({})),
    setUser,
  );
  usePromise(
    (signal) => getApplications({ signal, context }).catch(() => []),
    setApps,
  );
  usePromise(
    (signal) => getDevices({ signal, context }).catch(() => []),
    setDevices,
  );
  usePromise(
    (signal) =>
      features.isViewGroupsEnabled
        ? getGroups({ signal, context }).catch(() => [])
        : Promise.resolve([]),
    setGroups,
  );
  usePromise(
    (signal) =>
      features.isLinkedAccountsEnabled
        ? getLinkedAccounts({ signal, context }, { first: 0, max: 100, linked: true }).catch(
            () => [],
          )
        : Promise.resolve([]),
    setLinked,
  );

  const launchable = (apps ?? []).filter((a) => a.effectiveUrl);
  const quickLaunch = launchable.slice(0, QUICK_LAUNCH_LIMIT);
  const overflow = launchable.length - quickLaunch.length;

  const sessionCount = (devices ?? []).reduce((n, d) => n + (d.sessions?.length ?? 0), 0);
  const currentDevice = (devices ?? []).find((d) => d.current);
  const profileComplete = Boolean(user?.email && user?.firstName && user?.lastName);

  // Built-in clients carry localization placeholders as their name (e.g.
  // "${client_security-admin-console}") — label() resolves them against the
  // message bundle, same as the stock Applications page.
  const appName = (a: ClientRepresentation) =>
    label(t, a.clientName || a.clientId);

  return (
    <div className={style.hub}>
      <header className={style.hero}>
        <HexMark size={72} />
        <div className={style.heroText}>
          <div className={style.eyeRow}>
            <span className={style.eyebrow}>{t("hub.overview", "Account overview")}</span>
            <button
              className={style.signOut}
              type="button"
              onClick={() => keycloak.logout()}
            >
              {t("doSignOut", "Sign out")}
            </button>
          </div>
          <h1 className={style.welcome}>
            {t("hub.welcome", "Welcome back,")} <b>{user?.firstName ?? user?.username ?? ""}</b>
          </h1>
          <p className={style.sub}>
            {t(
              "hub.subtitle",
              "Everything about your identity, access, and devices — at a glance.",
            )}
          </p>
        </div>
      </header>

      {quickLaunch.length > 0 && (
        <section className={style.appsStrip} aria-label={t("hub.quickLaunch", "Quick launch")}>
          <h2>{t("hub.quickLaunch", "Quick launch")}</h2>
          <div className={style.chips}>
            {quickLaunch.map((a) => (
              <a
                key={a.clientId}
                className={style.chip}
                href={a.effectiveUrl}
                target="_blank"
                rel="noreferrer"
              >
                {appName(a)}
              </a>
            ))}
            {overflow > 0 && (
              <Link className={`${style.chip} ${style.chipMore}`} to="applications">
                +{overflow} {t("hub.more", "more")}
              </Link>
            )}
          </div>
        </section>
      )}

      <nav className={style.tiles} aria-label={t("hub.sections", "Account sections")}>
        <Link className={`${style.tile} ${style.wide}`} to="applications">
          <span className={style.ic}>
            <HexMark size={18} />
          </span>
          <h3>{t("applications", "Applications")}</h3>
          <p>{t("hub.applicationsHint", "Sign-in enabled services connected to your account.")}</p>
          <span className={style.num}>
            {apps ? launchable.length : "–"}
            <small>{t("hub.connected", "connected")}</small>
          </span>
          <span className={style.stat}>
            <span className={`${style.dot} ${style.dotGreen}`} />
            {t("hub.ssoReady", "All reachable via SSO")}
          </span>
        </Link>

        <Link className={style.tile} to="account-security/signing-in">
          <span className={style.ic} aria-hidden="true">🛡</span>
          <h3>{t("hub.security", "Security")}</h3>
          <p>{t("hub.securityHint", "Password, passkeys, and how you sign in.")}</p>
          <span className={`${style.stat} ${style.statBottom}`}>
            <span className={`${style.dot} ${style.dotBlue}`} />
            {t("hub.securityAction", "Manage sign-in settings")}
          </span>
        </Link>

        <Link className={style.tile} to="account-security/device-activity">
          <span className={style.ic} aria-hidden="true">💻</span>
          <h3>{t("hub.devices", "Devices")}</h3>
          <p>{t("hub.devicesHint", "Where you're signed in right now.")}</p>
          <span className={style.num}>
            {devices ? sessionCount : "–"}
            <small>{t("hub.sessions", "sessions")}</small>
          </span>
          <span className={style.stat}>
            <span className={`${style.dot} ${style.dotBlue}`} />
            {currentDevice
              ? `${t("hub.thisDevice", "This device")} · ${currentDevice.os ?? ""}`
              : t("hub.thisDevice", "This device")}
          </span>
        </Link>

        <Link className={style.tile} to="personal-info">
          <span className={style.ic} aria-hidden="true">👤</span>
          <h3>{t("personalInfo", "Personal info")}</h3>
          <p>{t("hub.personalInfoHint", "Name, email, and profile details.")}</p>
          <span className={`${style.stat} ${style.statBottom}`}>
            <span
              className={`${style.dot} ${profileComplete ? style.dotGreen : style.dotBlue}`}
            />
            {profileComplete
              ? t("hub.profileComplete", "Profile complete")
              : t("hub.profileIncomplete", "Add your details")}
          </span>
        </Link>

        {features.isViewGroupsEnabled && (
          <Link className={style.tile} to="groups">
            <span className={style.ic} aria-hidden="true">🗂</span>
            <h3>{t("groups", "Groups")}</h3>
            <p>{t("hub.groupsHint", "Team and role memberships.")}</p>
            <span className={style.num}>
              {groups ? groups.length : "–"}
              <small>{t("hub.memberships", "memberships")}</small>
            </span>
            <span className={style.stat}>
              <span className={`${style.dot} ${style.dotBlue}`} />
              {(groups ?? [])
                .slice(0, 2)
                .map((g) => g.name)
                .join(" · ") || " "}
            </span>
          </Link>
        )}

        {features.isLinkedAccountsEnabled && (
          <Link className={`${style.tile} ${style.wide}`} to="account-security/linked-accounts">
            <span className={style.ic} aria-hidden="true">🔗</span>
            <h3>{t("linkedAccounts", "Linked accounts")}</h3>
            <p>{t("hub.linkedHint", "External identities connected for sign-in.")}</p>
            <span className={style.num}>
              {linked ? linked.length : "–"}
              <small>{linked?.[0]?.displayName ?? t("hub.providers", "providers")}</small>
            </span>
            <span className={style.stat}>
              <span className={`${style.dot} ${style.dotGreen}`} />
              {t("hub.federationActive", "Federation active")}
            </span>
          </Link>
        )}
      </nav>
    </div>
  );
};
